import React, { useEffect, useState, useRef } from 'react';
import { Dashboard } from './components/Dashboard';
import { Navigation } from './components/Navigation';
import { Breadcrumbs, BreadcrumbItem } from './components/Breadcrumbs';
import { getAvailableLocales } from './components/utils/currencyFormatter';
import { VendorManagement } from './components/vendor/VendorManagement';
import { APManagement } from './components/ap/APManagement';
import { CreditNoteManagement } from './components/credit-notes/CreditNoteManagement';
import { UserManagement } from './components/settings/UserManagement';
import { LicenseManager } from './components/settings/LicenseManager';
import { ChartOfAccounts } from './components/financial/ChartOfAccounts';
import { LedgerView } from './components/financial/LedgerView';
import { CurrencyExchangeView } from './components/financial/CurrencyExchangeView';
import { ReceiptingView } from './components/procurement/ReceiptingView';
import { ProcurementDashboard } from './components/procurement/ProcurementDashboard';
import { DelegationsManager } from './components/financial/DelegationsManager';
import { RulesWorkflowManager } from './components/financial/RulesWorkflowManager';
import { RulesManager } from './components/financial/RulesManager';
import { WorkflowManager } from './components/financial/workflows/WorkflowManager';
import { CatalogsManagement } from './components/procurement/CatalogsManagement';
import { BudgetManagement } from './components/financial/budget/BudgetManagement';
import { TaxCodesManagement } from './components/financial/tax/TaxCodesManagement';
import { RoundingConfiguration } from './components/financial/rounding/RoundingConfiguration';
import { ToleranceConfiguration } from './components/financial/tolerance/ToleranceConfiguration';
import { TagsManagement } from './components/settings/tags/TagsManagement';
import { NotesManagement } from './components/settings/notes/NotesManagement';
import { AuditManagement } from './components/settings/audit/AuditManagement';
import { CountriesManagement } from './components/settings/countries/CountriesManagement';
import { CurrencyManagement } from './components/settings/currency/CurrencyManagement';
import { EmailConfiguration } from './components/settings/email/EmailConfiguration';
import { ReportsOverview } from './components/reports/ReportsOverview';
import { ReportViewer } from './components/reports/ReportViewer';
import { RequisitionForm } from './components/procurement/RequisitionForm';
import { PurchaseOrderForm } from './components/procurement/PurchaseOrderForm';
import { PurchaseOrderList } from './components/procurement/PurchaseOrderList';
import { RequisitionList } from './components/procurement/RequisitionList';
import { reportCategories } from './components/reports/ReportsOverview';
import { PaymentsView } from './components/financial/payments/PaymentsView';
import { ChevronDownIcon, LogOutIcon, GlobeIcon, UserIcon } from 'lucide-react';
import { DIExceptions } from './components/di/DIExceptions';
import { DIExceptionHandling } from './components/di/DIExceptionHandling';
import { DITraining } from './components/di/DITraining';
import { DocumentLibrary } from './components/di/DocumentLibrary';
import { FileLibrary } from './components/di/FileLibrary';
import { TasksManagement } from './components/di/TasksManagement';
import { DocumentManagementScreen } from './components/di/DocumentManagementScreen';
import { MessagesManagement } from './components/settings/messages/MessagesManagement';
import { NotificationsManagement } from './components/settings/notifications/NotificationsManagement';
import { ExpenseManagement } from './components/expense/ExpenseManagement';
import { ContractManagement } from './components/contracts/ContractManagement';
import { RCTIManagement } from './components/rcti/RCTIManagement';
import { DecimalPrecisionConfiguration } from './components/settings/decimal/DecimalPrecisionConfiguration';
import { AuthScreen } from './components/AuthScreen/AuthScreen';
import { CommunicationsView } from './components/financial/communications/CommunicationsView';
import { ContactsManagement } from './components/financial/ContactsManagement';
import { StatusManagement } from './components/settings/status/StatusManagement';
import { BankAccountsManagement } from './components/financial/BankAccountsManagement';
import { AddressesManagement } from './components/financial/AddressesManagement';
import { DocumentTraceabilityDemo } from './components/common/DocumentTraceability';
import { HomeIcon, FileTextIcon, UsersIcon, ShoppingCartIcon, ReceiptIcon, SettingsIcon, DollarSignIcon, FileBarChart2Icon, FileSignatureIcon, CreditCardIcon, BookIcon, BookOpenIcon, BuildingIcon, MailIcon, CoinsIcon, KeyIcon, TagIcon, MessageSquareIcon, ClipboardListIcon, CheckSquareIcon, AlertTriangleIcon, BarChartIcon, TruckIcon, DatabaseIcon, MinusSquareIcon, ChevronLeftIcon, FilterIcon, ClockIcon, RefreshCwIcon, FolderIcon, CheckIcon } from 'lucide-react';
import { ConstructionPaymentReport } from './components/reports/construction/ConstructionPaymentReport';
import { OperationalAnalyticsReport } from './components/reports/operational/OperationalAnalyticsReport';
import { InvoiceAgingReport } from './components/reports/ap/InvoiceAgingReport';
import { InvoicePaymentStatusReport } from './components/reports/ap/InvoicePaymentStatusReport';
import { PurchaseOrderSummary } from './components/reports/procurement/PurchaseOrderSummary';
import { BudgetVsActualReport } from './components/reports/financial/BudgetVsActualReport';
import { RequisitionSummaryReport } from './components/reports/requisition/RequisitionSummaryReport';
import { UserAuditReport } from './components/reports/audit/UserAuditReport';
import { ReceiptList } from './components/procurement/ReceiptList';
import { OrgUnitManagement } from './components/settings/orgunit/OrgUnitManagement';
import { DetachedViewer } from './components/di/DetachedViewer';
// Wrapper component to handle detached viewer routing
function AppRouter() {
  const [isDetachedViewer, setIsDetachedViewer] = useState(window.location.hash === '#detached-viewer');
  useEffect(() => {
    const handleHashChange = () => {
      setIsDetachedViewer(window.location.hash === '#detached-viewer');
    };
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);
  if (isDetachedViewer) {
    return <DetachedViewer data-id="element-0" />;
  }
  return <App data-id="element-1" />;
}
export { AppRouter };
export function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentView, setCurrentView] = useState('dashboard');
  const [selectedRequisition, setSelectedRequisition] = useState(null);
  const [selectedPOForReceipt, setSelectedPOForReceipt] = useState(null);
  const [locale, setLocale] = useState('en-AU');
  const [selectedReport, setSelectedReport] = useState(null);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const profileMenuRef = useRef(null);
  const profileButtonRef = useRef(null);
  const [isTaskDetailView, setIsTaskDetailView] = useState(false);
  const [requisitions, setRequisitions] = useState([{
    id: 'REQ-2023-001',
    name: 'Office Supplies',
    vendor: 'Office Depot',
    status: 'Draft',
    date: '2023-10-15',
    total: 1250.75
  }, {
    id: 'REQ-2023-002',
    name: 'IT Equipment',
    vendor: 'Dell Technologies',
    status: 'Pending Approval',
    date: '2023-10-12',
    total: 5430.0
  }, {
    id: 'REQ-2023-003',
    name: 'Marketing Materials',
    vendor: 'PrintWorks Inc.',
    status: 'Approved',
    date: '2023-10-05',
    total: 3200.5
  }]);
  const [purchaseOrders, setPurchaseOrders] = useState([{
    id: 'PO-2023-001',
    name: 'Server Hardware',
    vendor: 'Server Solutions Inc.',
    status: 'Sent to Vendor',
    date: '2023-10-10',
    total: 12500.0,
    requisitionId: null
  }, {
    id: 'PO-2023-002',
    name: 'Marketing Materials',
    vendor: 'PrintWorks Inc.',
    status: 'Pending Confirmation',
    date: '2023-10-06',
    total: 3200.5,
    requisitionId: 'REQ-2023-003'
  }]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  // Handler for task view changes
  const handleTaskViewChange = (view: 'list' | 'task-detail') => {
    setIsTaskDetailView(view === 'task-detail');
  };
  // Define view categories BEFORE getBreadcrumbs
  const isExpenseView = ['expense-management', 'expense-claims', 'expense-claim-detail', 'add-expense-claim', 'expense-approval', 'expense-reports'].includes(currentView);
  const isContractView = ['contract-management', 'contracts', 'onboard-contract', 'contract-details', 'add-contract', 'contract-detail', 'contract-variations', 'contract-amendments', 'contract-milestones', 'contract-payment-claims', 'contract-types'].includes(currentView);
  const isCreditNoteView = ['credit-note-dashboard', 'credit-note-entry', 'credit-note-list', 'credit-note-details'].includes(currentView);
  const isProcurementView = ['create-requisition', 'create-po', 'requisitions', 'purchase-orders', 'receipt-list', 'procurement-dashboard', 'catalogs'].includes(currentView);
  const isCommunicationsView = ['communications'].includes(currentView);
  const isSettingsView = ['settings', 'users', 'license-manager', 'tags', 'notes', 'audit', 'rules-workflows', 'countries', 'currency', 'email-configuration', 'messages', 'notifications', 'decimal-precision', 'status-management', 'document-traceability'].includes(currentView);
  const isReportsView = ['reports', 'reports-procurement', 'reports-ap', 'reports-requisition', 'reports-financial', 'reports-operational', 'reports-construction', 'reports-audit'].includes(currentView);
  const isFinancialView = ['chart-of-accounts', 'ledger', 'delegations', 'currency-exchange', 'budget', 'tax-codes', 'rounding', 'tolerance', 'payments', 'communications', 'rules', 'workflows', 'bank-accounts', 'addresses'].includes(currentView);
  const getBreadcrumbs = () => {
    const breadcrumbs: BreadcrumbItem[] = [];
    // Always start with Home
    breadcrumbs.push({
      label: 'Home',
      view: 'dashboard',
      icon: HomeIcon
    });
    // Add module-level breadcrumb based on current view
    if (currentView === 'vendor-management' || currentView === 'vendors' || currentView === 'onboard-vendor' || currentView === 'vendor-details' || currentView === 'compliance-check') {
      breadcrumbs.push({
        label: 'VM',
        view: 'vendor-management',
        icon: UsersIcon
      });
      if (currentView === 'vendor-management' || currentView === 'vendors') {
        breadcrumbs.push({
          label: 'Vendor Management',
          view: currentView,
          icon: UsersIcon
        });
      } else if (currentView === 'onboard-vendor') {
        breadcrumbs.push({
          label: 'Vendor Management',
          view: 'vendor-management',
          icon: UsersIcon
        });
        breadcrumbs.push({
          label: 'Add New Vendor',
          view: currentView,
          icon: UserIcon
        });
      }
    } else if (currentView === 'ap-management' || currentView === 'invoice-entry' || currentView === 'invoice-list' || currentView === 'matching' || currentView === 'statement-reconciliation' || currentView === 'ap-dashboard') {
      breadcrumbs.push({
        label: 'AP',
        view: 'ap-management',
        icon: ReceiptIcon
      });
      breadcrumbs.push({
        label: 'Accounts Payable',
        view: currentView,
        icon: ReceiptIcon
      });
    } else if (isExpenseView) {
      breadcrumbs.push({
        label: 'EM',
        view: 'expense-management',
        icon: CreditCardIcon
      });
      breadcrumbs.push({
        label: 'Expense Management',
        view: currentView,
        icon: CreditCardIcon
      });
    } else if (isProcurementView) {
      breadcrumbs.push({
        label: 'PO',
        view: 'procurement-dashboard',
        icon: ShoppingCartIcon
      });
      if (currentView === 'procurement-dashboard') {
        breadcrumbs.push({
          label: 'Procurement Dashboard',
          view: currentView,
          icon: ShoppingCartIcon
        });
      } else if (currentView === 'create-requisition') {
        breadcrumbs.push({
          label: 'Procurement',
          view: 'procurement-dashboard',
          icon: ShoppingCartIcon
        });
        breadcrumbs.push({
          label: 'Create Requisition',
          view: currentView,
          icon: FileTextIcon
        });
      } else if (currentView === 'create-po') {
        breadcrumbs.push({
          label: 'Procurement',
          view: 'procurement-dashboard',
          icon: ShoppingCartIcon
        });
        breadcrumbs.push({
          label: 'Create Purchase Order',
          view: currentView,
          icon: FileTextIcon
        });
      } else if (currentView === 'requisitions') {
        breadcrumbs.push({
          label: 'Procurement',
          view: 'procurement-dashboard',
          icon: ShoppingCartIcon
        });
        breadcrumbs.push({
          label: 'Requisitions',
          view: currentView,
          icon: ClipboardListIcon
        });
      } else if (currentView === 'purchase-orders') {
        breadcrumbs.push({
          label: 'Procurement',
          view: 'procurement-dashboard',
          icon: ShoppingCartIcon
        });
        breadcrumbs.push({
          label: 'Purchase Orders',
          view: currentView,
          icon: ClipboardListIcon
        });
      } else if (currentView === 'receipting') {
        breadcrumbs.push({
          label: 'Procurement',
          view: 'procurement-dashboard',
          icon: ShoppingCartIcon
        });
        breadcrumbs.push({
          label: 'Goods Receipt',
          view: currentView,
          icon: TruckIcon
        });
      } else if (currentView === 'receipt-list') {
        breadcrumbs.push({
          label: 'Procurement',
          view: 'procurement-dashboard',
          icon: ShoppingCartIcon
        });
        breadcrumbs.push({
          label: 'Goods Receipts',
          view: currentView,
          icon: ClipboardListIcon
        });
      } else if (currentView === 'catalogs') {
        breadcrumbs.push({
          label: 'Procurement',
          view: 'procurement-dashboard',
          icon: ShoppingCartIcon
        });
        breadcrumbs.push({
          label: 'Catalogs',
          view: currentView,
          icon: BookIcon
        });
      }
    } else if (isFinancialView) {
      breadcrumbs.push({
        label: 'FM',
        view: 'chart-of-accounts',
        icon: DollarSignIcon
      });
      // Add specific labels for financial management views
      const financialLabels = {
        'chart-of-accounts': 'Chart of Accounts',
        ledger: 'Ledger',
        delegations: 'Delegations',
        'currency-exchange': 'Currency Exchange',
        budget: 'Budget Management',
        'tax-codes': 'Tax Codes Management',
        rounding: 'Rounding Configuration',
        tolerance: 'Tolerance Configuration',
        payments: 'Payments',
        communications: 'Communications',
        rules: 'Rules',
        workflows: 'Workflows',
        'bank-accounts': 'Bank Accounts',
        addresses: 'Addresses',
        contacts: 'Contacts'
      };
      breadcrumbs.push({
        label: financialLabels[currentView] || 'Financial Management',
        view: currentView,
        icon: DollarSignIcon
      });
    } else if (isReportsView) {
      breadcrumbs.push({
        label: 'Reports',
        view: 'reports',
        icon: FileBarChart2Icon
      });
      if (selectedReport) {
        breadcrumbs.push({
          label: selectedReport.name,
          view: currentView,
          icon: FileTextIcon
        });
      }
    } else if (isSettingsView) {
      breadcrumbs.push({
        label: 'Settings',
        view: 'settings',
        icon: SettingsIcon
      });
      if (currentView !== 'settings') {
        const settingsLabels = {
          users: 'User Management',
          'license-manager': 'License Manager',
          tags: 'Tags',
          notes: 'Notes',
          audit: 'Audit Log',
          countries: 'Countries',
          currency: 'Currencies',
          'email-configuration': 'Email Configuration',
          messages: 'Messages',
          notifications: 'Notifications',
          'decimal-precision': 'Decimal Precision',
          'status-management': 'Status Management',
          'document-traceability': 'Document Traceability'
        };
        breadcrumbs.push({
          label: settingsLabels[currentView] || currentView,
          view: currentView,
          icon: SettingsIcon
        });
      }
    } else if (currentView === 'document-library' || currentView === 'file-library' || currentView === 'document-review' || currentView === 'tasks') {
      breadcrumbs.push({
        label: 'DI',
        view: 'tasks',
        icon: FileTextIcon
      });
      breadcrumbs.push({
        label: 'Document Intelligence',
        view: 'tasks',
        icon: FileTextIcon
      });
      if (currentView !== 'tasks') {
        const diLabels = {
          'document-library': 'Document Library',
          'file-library': 'File Library',
          'document-review': 'Document Review'
        };
        breadcrumbs.push({
          label: diLabels[currentView] || currentView,
          view: currentView,
          icon: FileTextIcon
        });
      }
    } else if (isContractView) {
      breadcrumbs.push({
        label: 'CM',
        view: 'contract-management',
        icon: FileSignatureIcon
      });
      breadcrumbs.push({
        label: 'Contract Management',
        view: currentView,
        icon: FileSignatureIcon
      });
    } else if (isCreditNoteView) {
      breadcrumbs.push({
        label: 'CN',
        view: 'credit-note-dashboard',
        icon: MinusSquareIcon
      });
      breadcrumbs.push({
        label: 'Credit Notes',
        view: currentView,
        icon: MinusSquareIcon
      });
    }
    return breadcrumbs;
  };
  // Custom setCurrentView that resets task detail view
  const handleSetCurrentView = (view: string) => {
    setCurrentView(view);
    setIsTaskDetailView(false);
  };
  useEffect(() => {
    const handleClickOutside = event => {
      if (profileMenuOpen && !profileButtonRef.current?.contains(event.target) && !profileMenuRef.current?.contains(event.target)) {
        setProfileMenuOpen(false);
      }
    };
    if (profileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [profileMenuOpen]);
  useEffect(() => {
    window.setSelectedReport = reportData => {
      console.log('Setting report via global function:', reportData);
      setSelectedReport({
        ...reportData,
        _timestamp: Date.now()
      });
    };
    if (currentView.startsWith('reports-') && !selectedReport) {
      const reportType = currentView.split('-')[1];
      const category = reportCategories.find(cat => cat.id === reportType);
      if (category && category.reports.length > 0) {
        console.log(`Setting default report for ${reportType} category`);
        const defaultReport = {
          ...category.reports[0],
          categoryId: category.id,
          _timestamp: Date.now()
        };
        setSelectedReport(defaultReport);
      }
    }
    return () => {
      delete window.setSelectedReport;
    };
  }, [currentView]);
  const handleSaveRequisition = (requisition, isDraft = true) => {
    const newRequisition = {
      id: `REQ-2023-00${requisitions.length + 1}`,
      name: requisition.name || 'Unnamed Requisition',
      vendor: requisition.vendor || 'Not Specified',
      status: isDraft ? 'Draft' : 'Pending Approval',
      date: new Date().toISOString().split('T')[0],
      total: requisition.total || 0
    };
    setRequisitions([...requisitions, newRequisition]);
    setCurrentView('dashboard');
  };
  const handleSavePurchaseOrder = (purchaseOrder, fromRequisition = null) => {
    const newPurchaseOrder = {
      id: `PO-2023-00${purchaseOrders.length + 1}`,
      name: purchaseOrder.name || 'Unnamed Purchase Order',
      vendor: purchaseOrder.vendor || 'Not Specified',
      status: 'Draft',
      date: new Date().toISOString().split('T')[0],
      total: purchaseOrder.total || 0,
      requisitionId: fromRequisition ? fromRequisition.id : null
    };
    setPurchaseOrders([...purchaseOrders, newPurchaseOrder]);
    if (fromRequisition) {
      setRequisitions(requisitions.map(req => req.id === fromRequisition.id ? {
        ...req,
        status: 'Converted to PO'
      } : req));
    }
    setCurrentView('purchase-orders');
  };
  const handleApproveRequisition = requisitionId => {
    setRequisitions(requisitions.map(req => req.id === requisitionId ? {
      ...req,
      status: 'Approved'
    } : req));
  };
  const handleConvertToPO = requisition => {
    setSelectedRequisition(requisition);
    setCurrentView('create-po-from-req');
  };
  const handleAutoConvertToPO = requisitionId => {
    const requisition = requisitions.find(req => req.id === requisitionId);
    if (requisition) {
      handleSavePurchaseOrder(requisition, requisition);
    }
  };
  const handleLocaleChange = e => {
    setLocale(e.target.value);
    setProfileMenuOpen(false);
  };
  const toggleProfileMenu = () => {
    setProfileMenuOpen(!profileMenuOpen);
  };
  const handleLogin = () => {
    setIsAuthenticated(true);
  };
  const handleLogout = () => {
    setIsAuthenticated(false);
    setProfileMenuOpen(false);
  };
  const refreshReport = () => {
    setIsLoading(true);
    setTimeout(() => setIsLoading(false), 800);
  };
  const renderLoadingState = () => <div className="flex items-center justify-center h-64" data-id="element-2">
      <div className="text-center" data-id="element-3">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500 mx-auto" data-id="element-4"></div>
        <p className="mt-3 text-sm text-gray-500" data-id="element-5">Loading report data...</p>
      </div>
    </div>;
  if (!isAuthenticated) {
    return <AuthScreen onLogin={handleLogin} data-id="element-6" />;
  }
  return <div className="min-h-screen bg-gray-50" data-id="element-7">
      <header className="bg-white shadow" data-id="element-8">
        <div className="w-[85%] mx-auto px-4 py-4 sm:px-6 lg:px-8 flex items-center justify-between" data-id="element-9">
          <div className="flex items-center" data-id="element-10">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/6TfLnmLX2evxxxZK6TYFtj/Rapid_P2P_-_Blue-Green.svg" alt="RapidP2P Logo" className="h-10" data-id="element-11" />
          </div>
          <div className="flex items-center space-x-4" data-id="element-12">
            <div className="relative" data-id="element-13">
              <button ref={profileButtonRef} onClick={toggleProfileMenu} className="flex items-center space-x-2 focus:outline-none" data-id="element-14">
                <div className="h-8 w-8 rounded-full overflow-hidden flex items-center justify-center bg-indigo-600 text-white text-sm" data-id="element-15">
                  <img src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80" alt="Profile" className="h-full w-full object-cover" onError={e => {
                  e.currentTarget.style.display = 'none';
                  e.currentTarget.parentElement.textContent = 'A';
                }} data-id="element-16" />
                </div>
                <span className="text-sm text-gray-500" data-id="element-17">Admin</span>
                <ChevronDownIcon className="h-4 w-4 text-gray-500" data-id="element-18" />
              </button>
              {profileMenuOpen && <div ref={profileMenuRef} className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-10 ring-1 ring-black ring-opacity-5" data-id="element-19">
                  <div className="px-4 py-2 text-xs text-gray-500 border-b border-gray-100" data-id="element-20">
                    admin@example.com
                  </div>
                  <div className="px-4 py-2 border-b border-gray-100" data-id="element-21">
                    <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-22">
                      Locale
                    </label>
                    <select value={locale} onChange={handleLocaleChange} className="block w-full pl-3 pr-10 py-1 text-sm border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 rounded-md" data-id="element-23">
                      {getAvailableLocales().map(localeOption => <option key={localeOption.code} value={localeOption.code} data-id="element-24">
                          {localeOption.name}
                        </option>)}
                    </select>
                  </div>
                  <a href="#" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center" data-id="element-25">
                    <UserIcon className="h-4 w-4 mr-2 text-gray-500" data-id="element-26" />
                    Profile Settings
                  </a>
                  <button onClick={handleLogout} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center" data-id="element-27">
                    <LogOutIcon className="h-4 w-4 mr-2 text-gray-500" data-id="element-28" />
                    Sign Out
                  </button>
                </div>}
            </div>
          </div>
        </div>
      </header>
      <Navigation currentView={currentView} setCurrentView={handleSetCurrentView} data-id="element-29" />
      <div className="w-[85%] mx-auto px-4 sm:px-6 lg:px-8 py-3 bg-gray-50 border-b border-gray-200" data-id="element-30">
        <Breadcrumbs items={getBreadcrumbs()} currentView={currentView} setCurrentView={handleSetCurrentView} data-id="element-31" />
      </div>
      <main className="w-[85%] mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-32">
        {currentView === 'dashboard' && <Dashboard requisitions={requisitions} purchaseOrders={purchaseOrders} setCurrentView={setCurrentView} locale={locale} data-id="element-33" />}
        {currentView === 'create-requisition' && <>
            <div className="mb-8" data-id="element-34">
              <h2 className="text-2xl font-bold text-gray-900" data-id="element-35">
                Create Requisition
              </h2>
              <p className="mt-1 text-sm text-gray-500" data-id="element-36">
                Fill in the details below to create a new purchase requisition
              </p>
            </div>
            <RequisitionForm onSave={handleSaveRequisition} locale={locale} data-id="element-37" />
          </>}
        {currentView === 'create-po' && <>
            <div className="mb-8" data-id="element-38">
              <h2 className="text-2xl font-bold text-gray-900" data-id="element-39">
                Create Purchase Order
              </h2>
              <p className="mt-1 text-sm text-gray-500" data-id="element-40">
                Fill in the details below to create a new purchase order
                directly
              </p>
            </div>
            <PurchaseOrderForm onSave={handleSavePurchaseOrder} locale={locale} data-id="element-41" />
          </>}
        {currentView === 'create-po-from-req' && selectedRequisition && <>
            <div className="mb-8" data-id="element-42">
              <h2 className="text-2xl font-bold text-gray-900" data-id="element-43">
                Convert Requisition to Purchase Order
              </h2>
              <p className="mt-1 text-sm text-gray-500" data-id="element-44">
                Review and modify details before creating the purchase order
              </p>
            </div>
            <PurchaseOrderForm initialData={selectedRequisition} onSave={data => handleSavePurchaseOrder(data, selectedRequisition)} isConversion={true} locale={locale} data-id="element-45" />
          </>}
        {currentView === 'requisitions' && <>
            <div className="mb-8" data-id="element-46">
              <h2 className="text-2xl font-bold text-gray-900" data-id="element-47">Requisitions</h2>
              <p className="mt-1 text-sm text-gray-500" data-id="element-48">
                View and manage all requisitions
              </p>
            </div>
            <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-49">
              <RequisitionList requisitions={requisitions} onApprove={handleApproveRequisition} onConvertToPO={handleConvertToPO} onAutoConvert={handleAutoConvertToPO} locale={locale} data-id="element-50" />
            </div>
          </>}
        {currentView === 'purchase-orders' && <>
            <div className="mb-8 flex justify-between items-center" data-id="element-51">
              <div data-id="element-52">
                <h2 className="text-2xl font-bold text-gray-900" data-id="element-53">
                  Purchase Orders
                </h2>
                <p className="mt-1 text-sm text-gray-500" data-id="element-54">
                  View and manage all purchase orders
                </p>
              </div>
              <div className="flex space-x-2" data-id="element-55">
                <button onClick={() => setCurrentView('receipt-list')} className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-56">
                  <TruckIcon className="h-4 w-4 mr-1" data-id="element-57" />
                  View Receipts
                </button>
                <button onClick={() => setCurrentView('create-po')} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-58">
                  Create Purchase Order
                </button>
              </div>
            </div>
            <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-59">
              <PurchaseOrderList purchaseOrders={purchaseOrders} locale={locale} onCreateReceipt={poId => {
            const selectedPO = purchaseOrders.find(po => po.id === poId);
            setSelectedPOForReceipt(selectedPO);
            setCurrentView('receipt-list');
          }} data-id="element-60" />
            </div>
          </>}
        {currentView === 'receipt-list' && <>
            <div className="mb-8 flex justify-between items-center" data-id="element-61">
              <div data-id="element-62">
                <h2 className="text-2xl font-bold text-gray-900" data-id="element-63">
                  Goods Receipts
                </h2>
                <p className="mt-1 text-sm text-gray-500" data-id="element-64">
                  View and manage all goods receipts
                </p>
              </div>
            </div>
            <ReceiptList locale={locale} onCreateReceipt={() => {
          setSelectedPOForReceipt(null);
          // Navigate to receipting view to create a new receipt
          setCurrentView('receipting');
        }} onViewReceiptDetails={receipt => {
          setSelectedReceipt(receipt);
        }} data-id="element-65" />
          </>}
        {currentView === 'receipting' && <ReceiptingView poId={selectedPOForReceipt?.id} initialPO={selectedPOForReceipt} onBack={() => setCurrentView(selectedPOForReceipt ? 'purchase-orders' : 'receipt-list')} locale={locale} data-id="element-66" />}
        {currentView === 'vendor-management' && <VendorManagement locale={locale} currentView={currentView} data-id="element-67" />}
        {currentView === 'vendors' && <VendorManagement locale={locale} currentView={currentView} data-id="element-68" />}
        {currentView === 'onboard-vendor' && <VendorManagement locale={locale} currentView={currentView} data-id="element-69" />}
        {currentView === 'vendor-details' && <VendorManagement locale={locale} currentView={currentView} data-id="element-70" />}
        {currentView === 'compliance-check' && <VendorManagement locale={locale} currentView={currentView} data-id="element-71" />}
        {(currentView === 'ap-management' || currentView === 'invoice-entry' || currentView === 'invoice-list' || currentView === 'matching' || currentView === 'statement-reconciliation' || currentView === 'ap-dashboard') && <APManagement locale={locale} currentView={currentView} data-id="element-72" />}
        {(currentView === 'rcti-list' || currentView === 'rcti-entry' || currentView === 'rcti-details') && <RCTIManagement locale={locale} currentView={currentView} setCurrentView={setCurrentView} data-id="element-73" />}
        {currentView === 'rcti-management' && <RCTIManagement locale={locale} currentView={currentView} setCurrentView={setCurrentView} data-id="element-74" />}
        {currentView === 'settings' && <OrgUnitManagement locale={locale} data-id="element-75" />}
        {currentView === 'users' && <UserManagement locale={locale} data-id="element-76" />}
        {currentView === 'license-manager' && <LicenseManager locale={locale} data-id="element-77" />}
        {currentView === 'countries' && <CountriesManagement locale={locale} data-id="element-78" />}
        {currentView === 'currency' && <CurrencyManagement locale={locale} data-id="element-79" />}
        {currentView === 'email-configuration' && <EmailConfiguration locale={locale} data-id="element-80" />}
        {currentView === 'chart-of-accounts' && <ChartOfAccounts locale={locale} data-id="element-81" />}
        {currentView === 'ledger' && <LedgerView locale={locale} data-id="element-82" />}
        {currentView === 'payments' && <PaymentsView locale={locale} data-id="element-83" />}
        {currentView === 'currency-exchange' && <CurrencyExchangeView locale={locale} data-id="element-84" />}
        {currentView === 'delegations' && <DelegationsManager locale={locale} data-id="element-85" />}
        {currentView === 'rules-workflows' && <RulesWorkflowManager locale={locale} data-id="element-86" />}
        {currentView === 'procurement-dashboard' && <ProcurementDashboard requisitions={requisitions} purchaseOrders={purchaseOrders} setCurrentView={setCurrentView} locale={locale} data-id="element-87" />}
        {currentView === 'catalogs' && <CatalogsManagement locale={locale} data-id="element-88" />}
        {currentView === 'budget' && <BudgetManagement locale={locale} data-id="element-89" />}
        {currentView === 'tax-codes' && <TaxCodesManagement locale={locale} data-id="element-90" />}
        {currentView === 'rounding' && <RoundingConfiguration locale={locale} data-id="element-91" />}
        {currentView === 'tolerance' && <ToleranceConfiguration locale={locale} data-id="element-92" />}
        {currentView === 'tags' && <TagsManagement locale={locale} data-id="element-93" />}
        {currentView === 'notes' && <NotesManagement locale={locale} data-id="element-94" />}
        {currentView === 'audit' && <AuditManagement locale={locale} data-id="element-95" />}
        {currentView === 'reports' && <ReportsOverview setCurrentView={setCurrentView} setSelectedReport={setSelectedReport} locale={locale} data-id="element-96" />}
        {currentView === 'reports-construction' && selectedReport && <div className="construction-report-container" data-id="element-97">
            <div className="mb-6 flex items-center justify-between" data-id="element-98">
              <div data-id="element-99">
                <h2 className="text-2xl font-bold text-gray-900" data-id="element-100">
                  Construction Payment Report
                </h2>
                <p className="mt-1 text-sm text-gray-500" data-id="element-101">
                  {selectedReport.description}
                </p>
              </div>
              <div className="flex space-x-2" data-id="element-102">
                <button onClick={refreshReport} className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-103">
                  <RefreshCwIcon className="h-4 w-4 mr-1" data-id="element-104" />
                  Refresh
                </button>
                <button onClick={() => {
              setSelectedReport(null);
              setCurrentView('reports');
            }} className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-105">
                  <ChevronLeftIcon className="h-4 w-4 mr-1" data-id="element-106" />
                  Back to Reports
                </button>
              </div>
            </div>
            {isLoading ? renderLoadingState() : <ConstructionPaymentReport filters={{
          dateRange: 'last30days',
          startDate: new Date(Date.now() - 30 * 86400000).toISOString().split('T')[0],
          endDate: new Date().toISOString().split('T')[0]
        }} locale={locale} data-id="element-107" />}
          </div>}
        {currentView === 'reports-operational' && selectedReport && <div className="operational-report-container" data-id="element-108">
            <div className="mb-6 flex items-center justify-between" data-id="element-109">
              <div data-id="element-110">
                <h2 className="text-2xl font-bold text-gray-900" data-id="element-111">
                  Operational Analytics Report
                </h2>
                <p className="mt-1 text-sm text-gray-500" data-id="element-112">
                  {selectedReport.description}
                </p>
              </div>
              <button onClick={() => {
            setSelectedReport(null);
            setCurrentView('reports');
          }} className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-113">
                <ChevronLeftIcon className="h-4 w-4 mr-1" data-id="element-114" />
                Back to Reports
              </button>
            </div>
            {isLoading ? renderLoadingState() : <OperationalAnalyticsReport filters={{
          dateRange: 'last30days',
          startDate: new Date(Date.now() - 30 * 86400000).toISOString().split('T')[0],
          endDate: new Date().toISOString().split('T')[0]
        }} locale={locale} report={selectedReport} data-id="element-115" />}
          </div>}
        {currentView === 'reports-ap' && selectedReport && <div className="ap-report-container" data-id="element-116">
            <div className="mb-6 flex items-center justify-between" data-id="element-117">
              <div data-id="element-118">
                <h2 className="text-2xl font-bold text-gray-900" data-id="element-119">
                  {selectedReport.id === 'invoice-aging' ? 'Invoice Aging Report' : 'Invoice Payment Status Report'}
                </h2>
                <p className="mt-1 text-sm text-gray-500" data-id="element-120">
                  {selectedReport.description}
                </p>
              </div>
              <button onClick={() => {
            setSelectedReport(null);
            setCurrentView('reports');
          }} className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-121">
                <ChevronLeftIcon className="h-4 w-4 mr-1" data-id="element-122" />
                Back to Reports
              </button>
            </div>
            {isLoading ? renderLoadingState() : selectedReport.id === 'invoice-aging' ? <InvoiceAgingReport filters={{
          dateRange: 'last30days',
          startDate: new Date(Date.now() - 30 * 86400000).toISOString().split('T')[0],
          endDate: new Date().toISOString().split('T')[0]
        }} locale={locale} data-id="element-123" /> : <InvoicePaymentStatusReport filters={{
          dateRange: 'last30days',
          startDate: new Date(Date.now() - 30 * 86400000).toISOString().split('T')[0],
          endDate: new Date().toISOString().split('T')[0]
        }} locale={locale} data-id="element-124" />}
          </div>}
        {currentView === 'reports-procurement' && selectedReport && <div className="procurement-report-container" data-id="element-125">
            <div className="mb-6 flex items-center justify-between" data-id="element-126">
              <div data-id="element-127">
                <h2 className="text-2xl font-bold text-gray-900" data-id="element-128">
                  Purchase Order Summary Report
                </h2>
                <p className="mt-1 text-sm text-gray-500" data-id="element-129">
                  {selectedReport.description}
                </p>
              </div>
              <button onClick={() => {
            setSelectedReport(null);
            setCurrentView('reports');
          }} className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-130">
                <ChevronLeftIcon className="h-4 w-4 mr-1" data-id="element-131" />
                Back to Reports
              </button>
            </div>
            {isLoading ? renderLoadingState() : <PurchaseOrderSummary filters={{
          dateRange: 'last30days',
          startDate: new Date(Date.now() - 30 * 86400000).toISOString().split('T')[0],
          endDate: new Date().toISOString().split('T')[0]
        }} locale={locale} data-id="element-132" />}
          </div>}
        {currentView === 'reports-financial' && selectedReport && <div className="financial-report-container" data-id="element-133">
            <div className="mb-6 flex items-center justify-between" data-id="element-134">
              <div data-id="element-135">
                <h2 className="text-2xl font-bold text-gray-900" data-id="element-136">
                  Budget vs Actual Report
                </h2>
                <p className="mt-1 text-sm text-gray-500" data-id="element-137">
                  {selectedReport.description}
                </p>
              </div>
              <button onClick={() => {
            setSelectedReport(null);
            setCurrentView('reports');
          }} className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-138">
                <ChevronLeftIcon className="h-4 w-4 mr-1" data-id="element-139" />
                Back to Reports
              </button>
            </div>
            {isLoading ? renderLoadingState() : <BudgetVsActualReport filters={{
          dateRange: 'last30days',
          startDate: new Date(Date.now() - 30 * 86400000).toISOString().split('T')[0],
          endDate: new Date().toISOString().split('T')[0]
        }} locale={locale} data-id="element-140" />}
          </div>}
        {currentView === 'reports-requisition' && selectedReport && <div className="requisition-report-container" data-id="element-141">
            <div className="mb-6 flex items-center justify-between" data-id="element-142">
              <div data-id="element-143">
                <h2 className="text-2xl font-bold text-gray-900" data-id="element-144">
                  Requisition Summary Report
                </h2>
                <p className="mt-1 text-sm text-gray-500" data-id="element-145">
                  {selectedReport.description}
                </p>
              </div>
              <button onClick={() => {
            setSelectedReport(null);
            setCurrentView('reports');
          }} className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-146">
                <ChevronLeftIcon className="h-4 w-4 mr-1" data-id="element-147" />
                Back to Reports
              </button>
            </div>
            {isLoading ? renderLoadingState() : <RequisitionSummaryReport filters={{
          dateRange: 'last30days',
          startDate: new Date(Date.now() - 30 * 86400000).toISOString().split('T')[0],
          endDate: new Date().toISOString().split('T')[0]
        }} locale={locale} data-id="element-148" />}
          </div>}
        {currentView === 'reports-audit' && selectedReport && <div className="audit-report-container" data-id="element-149">
            <div className="mb-6 flex items-center justify-between" data-id="element-150">
              <div data-id="element-151">
                <h2 className="text-2xl font-bold text-gray-900" data-id="element-152">
                  User Audit Trail
                </h2>
                <p className="mt-1 text-sm text-gray-500" data-id="element-153">
                  {selectedReport.description}
                </p>
              </div>
              <button onClick={() => {
            setSelectedReport(null);
            setCurrentView('reports');
          }} className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-154">
                <ChevronLeftIcon className="h-4 w-4 mr-1" data-id="element-155" />
                Back to Reports
              </button>
            </div>
            {isLoading ? renderLoadingState() : <UserAuditReport locale={locale} report={selectedReport} data-id="element-156" />}
          </div>}
        {currentView === 'decimal-precision' && <DecimalPrecisionConfiguration locale={locale} data-id="element-157" />}
        {currentView === 'document-review' && <DocumentManagementScreen data-id="element-158" />}
        {currentView === 'di-exceptions' && <DIExceptionHandling locale={locale} data-id="element-159" />}
        {currentView === 'di-training' && <DITraining locale={locale} data-id="element-160" />}
        {currentView === 'document-library' && <DocumentLibrary locale={locale} data-id="element-161" />}
        {currentView === 'file-library' && <FileLibrary locale={locale} data-id="element-162" />}
        {currentView === 'tasks' && <TasksManagement locale={locale} onViewChange={handleTaskViewChange} data-id="element-163" />}
        {currentView === 'messages' && <MessagesManagement locale={locale} data-id="element-164" />}
        {currentView === 'notifications' && <NotificationsManagement locale={locale} data-id="element-165" />}
        {isExpenseView && <ExpenseManagement locale={locale} currentView={currentView} data-id="element-166" />}
        {isContractView && <ContractManagement locale={locale} currentView={currentView} data-id="element-167" />}
        {isCreditNoteView && <CreditNoteManagement locale={locale} currentView={currentView} data-id="element-168" />}
        {currentView === 'communications' && <CommunicationsView locale={locale} data-id="element-169" />}
        {currentView === 'contacts' && <ContactsManagement locale={locale} data-id="element-170" />}
        {currentView === 'bank-accounts' && <BankAccountsManagement locale={locale} data-id="element-171" />}
        {currentView === 'addresses' && <AddressesManagement locale={locale} data-id="element-172" />}
        {currentView === 'status-management' && <StatusManagement locale={locale} data-id="element-173" />}
        {currentView === 'document-traceability' && <div data-id="element-174">
            <div className="mb-8" data-id="element-175">
              <h2 className="text-2xl font-bold text-gray-900" data-id="element-176">
                Document Traceability
              </h2>
              <p className="mt-1 text-sm text-gray-500" data-id="element-177">
                Business History and Regulatory Audit Trail for document
                lifecycle tracking
              </p>
            </div>
            <DocumentTraceabilityDemo data-id="element-178" />
          </div>}
        {currentView === 'rules' && <RulesManager locale={locale} data-id="element-179" />}
        {currentView === 'workflows' && <WorkflowManager locale={locale} data-id="element-180" />}
      </main>
    </div>;
}